import Table, { Body, Cell, Head, Row } from "@storeblocks/table-static";
import {
  InsuranceType,
  ProductBackendName,
} from "Components/Features/CheckPrice/Products/productTypes";
import { InsuranceCoverage } from "Store/interfaces/products/coverage";
import { useComponentTexts, useInsuranceCoverages } from "Store/texts/hooks";
import React from "react";
import { IsIncludedCoverage } from "./IsIncludedCoverage";
import {
  dropIrrelevantCoverage,
  getCoveragesFromInsuranceType,
  getRelevantHeaders,
  insuranceTypeFromInsuranceCoverage,
} from "./coverages.helpers";
import {
  CoverageContent,
  OverflowBox,
  SelectableCell,
} from "./coverages.styles";

interface Props {
  readonly selected: InsuranceCoverage;
  readonly product: ProductBackendName;
  readonly doNotShow?: InsuranceType;
}

export const CoverageComparisonTable = ({
  product,
  selected,
  doNotShow,
}: Props): React.ReactElement => {
  const [{ coverageAccordion2 }, lang] = useComponentTexts();
  const coverages = dropIrrelevantCoverage(
    useInsuranceCoverages(product),
    insuranceTypeFromInsuranceCoverage(selected),
    doNotShow,
  );

  const isInsuranceTypeSelected = (insuranceType: InsuranceType): boolean =>
    getCoveragesFromInsuranceType(insuranceType).includes(selected);

  const headers = getRelevantHeaders(coverageAccordion2, product, coverages);

  return (
    <OverflowBox>
      <Table>
        <Head>
          <Row>
            <Cell colSpan={2}>{coverageAccordion2.header[lang]}</Cell>
            {headers.map(([insuranceType, text]) => (
              <SelectableCell
                key={insuranceType}
                colSpan={1}
                selected={isInsuranceTypeSelected(insuranceType)}
              >
                {text[lang]}
              </SelectableCell>
            ))}
          </Row>
        </Head>
        <Body>
          {coverages.map((coverage) => (
            <Row
              key={coverage.caption[lang]}
              expand={
                <Row>
                  <Cell colSpan={headers.length + 2}>
                    {coverage.explanation[lang].map((explanation) => (
                      <CoverageContent key={explanation}>
                        {explanation}
                      </CoverageContent>
                    ))}
                  </Cell>
                </Row>
              }
            >
              <Cell>{coverage.caption[lang]}</Cell>
              {headers.map(([insuranceType]) => (
                <SelectableCell
                  key={insuranceType}
                  selected={isInsuranceTypeSelected(insuranceType)}
                >
                  <IsIncludedCoverage included={coverage[insuranceType]} />
                </SelectableCell>
              ))}
            </Row>
          ))}
        </Body>
      </Table>
    </OverflowBox>
  );
};
